// Desktop navigation rail: section links (Trips/Discover/Bucket/You),
// upcoming-trip shortcuts, "Plan a new trip" CTA, signed-in identity and
// sign-out. Mobile uses UI.TabBar instead.
import React from 'react';
import { PALETTE } from '../theme';
import { Icon } from './UI';
import { daysUntil, fmtDMY, sortByDateStart } from '../utils/trips';
import { isPastTrip } from '../utils/visited';

const NAV = [
  { id: 'home',    label: 'Trips',       icon: 'home' },
  { id: 'discover',label: 'Discover',    icon: 'compass' },
  { id: 'map',     label: 'World Map',   icon: 'globe' },
  { id: 'bucket',  label: 'Travel Inspiration', icon: 'star' },
  { id: 'profile', label: 'You',         icon: 'user' },
];

// A trip is still part of the Trips area. Without this, the last Home section
// (for example, Discover) stayed highlighted while someone was editing a trip.
export function activeSidebarTab(screen, homeTab) {
  return screen === 'home' ? homeTab : 'home';
}

export function Sidebar({ screen, homeTab, onNav, trips, onOpenTrip, onNewTrip, user, onLogout }) {
  const activeTab = activeSidebarTab(screen, homeTab);

  return (
    <aside style={{
      width: 260,
      flexShrink: 0,
      height: '100vh',
      position: 'sticky',
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      background: PALETTE.paperDim,
      borderRight: `1px solid ${PALETTE.rule}`,
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <div style={{ padding: '28px 24px 20px' }}>
        <div style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 22, fontWeight: 500, letterSpacing: -0.5, color: PALETTE.ink,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ fontSize: 20 }}>✈</span>
          <span>Travel Planner</span>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV.map(n => {
          const active = n.id === activeTab;
          return (
            <button key={n.id} type="button" onClick={() => onNav(n.id)} aria-current={active ? 'page' : undefined} style={{
              appearance: 'none', border: 0, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 10,
              background: active ? PALETTE.accent : 'transparent',
              color: active ? '#fff' : PALETTE.inkSoft,
              fontFamily: 'Inter, system-ui', fontSize: 14, fontWeight: active ? 600 : 400,
              boxShadow: active ? '0 2px 8px rgba(184,82,48,0.25)' : 'none',
              transition: 'all 0.15s', textAlign: 'left',
            }}
            onMouseEnter={e => { if (!active) { e.currentTarget.style.background = PALETTE.ruleSoft; e.currentTarget.style.color = PALETTE.ink; } }}
            onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PALETTE.inkSoft; } }}
            >
              <Icon name={n.icon} size={18} stroke={active ? 2 : 1.5} />
              {n.label}
            </button>
          );
        })}
      </nav>

      {/* Divider */}
      <div style={{ height: 1, background: PALETTE.rule, margin: '16px 24px' }} />

      {/* Trips list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px' }}>
        {trips.length === 0 ? (
          <div style={{ padding: '8px 12px', fontFamily: 'Fraunces', fontSize: 13, fontStyle: 'italic', color: PALETTE.inkMute, lineHeight: 1.5 }}>
            No trips yet. Plan your first adventure.
          </div>
        ) : (
          <>
            <div style={{
              fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5,
              textTransform: 'uppercase', color: PALETTE.inkMute,
              padding: '0 12px 8px',
            }}>Upcoming</div>
            {sortByDateStart(trips.filter(t => !isPastTrip(t))).map(t => (
              <SidebarTripItem key={t.id} trip={t} onOpen={() => onOpenTrip(t.id)} />
            ))}
            {trips.some(t => isPastTrip(t)) && (
              <>
                <div style={{
                  fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5,
                  textTransform: 'uppercase', color: PALETTE.inkMute,
                  padding: '12px 12px 8px',
                }}>Past</div>
                {trips.filter(t => isPastTrip(t)).map(t => (
                  <SidebarTripItem key={t.id} trip={t} onOpen={() => onOpenTrip(t.id)} />
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* New trip */}
      <div style={{ padding: '12px 12px 12px' }}>
        <button type="button" onClick={onNewTrip} style={{
          appearance: 'none', border: 0, cursor: 'pointer',
          width: '100%', padding: '11px 14px',
          background: PALETTE.ink, color: PALETTE.paper,
          borderRadius: 10,
          fontFamily: 'Inter', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          transition: 'opacity 0.15s',
        }}>
          <Icon name="plus" size={16} stroke={2} />
          Plan a new trip
        </button>
      </div>

      {/* Account / logout */}
      {user && (
        <div style={{ padding: '0 12px 20px', borderTop: `1px solid ${PALETTE.rule}`, paddingTop: 12 }}>
          <div style={{ padding: '6px 12px', marginBottom: 4 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 2 }}>Signed in as</div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
          </div>
          <button type="button" onClick={onLogout} style={{
            appearance: 'none', border: `1px solid ${PALETTE.rule}`, cursor: 'pointer',
            width: '100%', padding: '8px 14px',
            background: 'transparent', color: PALETTE.inkSoft,
            borderRadius: 10,
            fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = PALETTE.ruleSoft; e.currentTarget.style.color = PALETTE.ink; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = PALETTE.inkSoft; }}
          >
            <Icon name="arrowL" size={14} stroke={1.5} />
            Sign out
          </button>
        </div>
      )}
    </aside>
  );
}

function SidebarTripItem({ trip, onOpen }) {
  const cd = daysUntil(trip.dateStart);
  return (
    <button type="button" onClick={onOpen} aria-label={`Open ${trip.title || trip.destination?.split(',')[0] || 'trip'}`} style={{
      appearance: 'none', border: 0, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 10,
      width: '100%', padding: '8px 12px', borderRadius: 8,
      background: 'transparent', textAlign: 'left',
      transition: 'background 0.12s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = PALETTE.paper}
    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 6, flexShrink: 0,
        background: trip.swatch || 'linear-gradient(135deg, #E8C9A0, #D08B5C)',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Inter', fontSize: 13, fontWeight: 500,
          color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{trip.title || trip.destination?.split(',')[0]}</div>
        <div style={{
          fontFamily: 'JetBrains Mono', fontSize: 9,
          color: cd != null && cd <= 7 ? PALETTE.accent : PALETTE.inkMute,
          letterSpacing: 0.5, marginTop: 2,
        }}>
          {cd != null ? (cd === 0 ? 'Today' : `${cd}d away`) : (fmtDMY(trip.dateStart) || 'No date')}
        </div>
      </div>
    </button>
  );
}
